import { useTranslation } from "@sagot/i18n";
import { CardTimeout } from "../builder/card-forms";
import { ExitsEditor } from "../builder/exits-editor";
import type { FlowContext } from "../builder/flow-context";
import type { FormProps } from "../builder/webhook-form";
import { TypeField } from "./service-fields";
import type { ShowMyTicketsStep, ShowTicketFormStep, TicketIntakeStep } from "./service-types";

const F = "engage.workflows.service.fields";

type Flow = Pick<FlowContext, "paths" | "canAddPath">;

/** Ticket intake: the assistant takes the concern, checks for an open request about it, and creates the ticket only when the client presses Submit. */
export function TicketIntakeForm({ step, disabled, onChange, paths, canAddPath }: FormProps<TicketIntakeStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.intakeHelp`)}</p>
      <TypeField step={step} disabled={disabled} emptyLabel={t(`${F}.intakeAnyType`)} label={t(`${F}.ticketType`)} hint={t(`${F}.intakeTypeHint`)} onChange={(next) => { onChange({ ...step, type_id: next.type_id, type_key: next.type_key }); }} />
      <CardTimeout legend={t(`${F}.intakeTimeout`)} hint={t(`${F}.intakeTimeoutHint`)} value={step.abandoned_after} disabled={disabled} onChange={(abandoned_after) => { onChange({ ...step, abandoned_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Show ticket form: the static form of one ticket type, no assistant. */
export function ShowTicketFormForm({ step, disabled, onChange, paths, canAddPath }: FormProps<ShowTicketFormStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.formHelp`)}</p>
      <TypeField step={step} disabled={disabled} label={t(`${F}.ticketType`)} hint={t(`${F}.formTypeHint`)} onChange={(next) => { onChange({ ...step, type_id: next.type_id, type_key: next.type_key }); }} />
      <CardTimeout legend={t(`${F}.formTimeout`)} hint={t(`${F}.formTimeoutHint`)} value={step.not_submitted_after} disabled={disabled} onChange={(not_submitted_after) => { onChange({ ...step, not_submitted_after }); }} />
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}

/** Show my tickets: the verified client's open requests, five at most, with a link to My tickets; a client who is not verified is asked first. */
export function ShowMyTicketsForm({ step, disabled, onChange, paths, canAddPath }: FormProps<ShowMyTicketsStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.myTicketsHelp`)}</p>
      <ExitsEditor step={step} onChange={onChange} paths={paths} canAddPath={canAddPath} disabled={disabled} />
    </div>
  );
}
